import { ref } from 'vue'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { getCookie } from '@/utils/cookies'

const { setRealtimeStream } = useStore('common');

class RealtimeStreamManager {
  private abortController: AbortController | null = null
  private reconnectTimer: number | null = null
  private reconnectDelay = 3000
  
  public isConnected = ref(false)
  public isConnecting = ref(false)
  
  // 获取 token
  private getToken(): string | null {
    return getCookie('baai-data-admin-token-key') || null
  }
  
  async connect() {
    if (this.abortController || this.isConnecting.value) return
    
    this.isConnecting.value = true
    
    const token = this.getToken()
    if (!token) {
      console.warn('[SSE] 未找到 token，跳过连接')
      this.isConnecting.value = false
      return
    }
    
    this.abortController = new AbortController()
    
    try {
      await fetchEventSource('/flagos/api/v1/realtime/stream', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,  // 自定义 Authorization header
          'Content-Type': 'application/json'
        },
        signal: this.abortController.signal,
        
        onopen: async (response) => {
          if (response.ok) {
            console.log('[SSE] 连接成功')
            this.isConnected.value = true
            this.isConnecting.value = false
            this.reconnectDelay = 3000
          } else if (response.status === 401) {
            console.error('[SSE] 认证失败')
            this.handleAuthError()
            throw new Error('Unauthorized')
          } else {
            throw new Error(`连接失败: ${response.status}`)
          }
        },
        
        onmessage: (event) => {
          if (event) {
            try {
              setRealtimeStream(event.event || '')
            } catch (e) {
              console.error('[SSE] 解析消息失败:', e)
            }
          }
        },
        
        onerror: (err) => {
          console.error('[SSE] 连接错误:', err)
          this.handleError()
          throw err  // 抛出错误会触发重连
        },
        
        onclose: () => {
          console.log('[SSE] 连接关闭')
          this.handleError()
        }
      })
      
    } catch (error) {
      console.error('[SSE] 创建连接失败:', error)
      this.handleError()
    }
  }
  
  // 处理认证失败
  private handleAuthError() {
    this.disconnect()
    // 可以触发登出或刷新 token 的逻辑
    window.dispatchEvent(new CustomEvent('realtime:unauthorized'))
  }
  
  private handleError() {
    this.isConnected.value = false
    this.isConnecting.value = false
    
    if (this.abortController) {
      this.abortController = null
    }
    
    this.scheduleReconnect()
  }
  
  private scheduleReconnect() {
    if (this.reconnectTimer) return
    
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      console.log(`[SSE] 尝试重连 (${this.reconnectDelay}ms)`)
      this.connect()
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 60000)
    }, this.reconnectDelay)
  }
  
  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    
    this.isConnected.value = false
    this.isConnecting.value = false
  }
  
  // 注册刷新回调
  onRefresh(target: string, callback: () => void | Promise<void>) {
    // ... 实现同之前
  }
}

export const realtimeStream = new RealtimeStreamManager()